import React from 'react';
import { ReservationsDataOutDTO } from '@api/ringil-api.v2';
import { CalendarSurface, FragmentConfig, LayoutZone, SurfaceLayout, zoneItems } from '../model';
import { CalendarStateStyle } from '../stateStyle';
import { rendererFor } from './registry';

export type FragmentItemWrapper = (config: FragmentConfig, zone: LayoutZone, node: React.ReactNode) => React.ReactNode;

interface FragmentZoneProps {
  surface: CalendarSurface;
  layout: SurfaceLayout;
  zone: LayoutZone;
  data: ReservationsDataOutDTO;
  stateStyle: CalendarStateStyle;
  onStateChange?: () => void;
  /** Used only by the layout designer, to hang drag handles and selection on each rendered fragment. */
  itemWrapper?: FragmentItemWrapper;
}

/**
 * Renders one zone's fragments in configured order.
 *
 * Deliberately emits no wrapper of its own — the surface owns the zone container and its layout
 * (the header row is a flex row, the body a flex column), so this only supplies the children.
 * Ordering is plain DOM order, which is why the card no longer needs the `order-*` utility classes
 * it used to compute from the fragment list.
 */
export const FragmentZone: React.FC<FragmentZoneProps> = ({ surface, layout, zone, data, stateStyle, onStateChange, itemWrapper }) => (
  <>
    {zoneItems(layout, zone).map(config => {
      const Renderer = rendererFor(config.key, surface);
      // A fragment the surface cannot render (or that this build no longer knows) is skipped
      // rather than breaking the whole card.
      if (!Renderer) return null;
      const node = <Renderer data={data} config={config} stateStyle={stateStyle} onStateChange={onStateChange} />;
      return <React.Fragment key={config.key}>{itemWrapper ? itemWrapper(config, zone, node) : node}</React.Fragment>;
    })}
  </>
);
